import { useCallback, useRef, useState } from 'react';
import { apiDownloadBlob, apiPostForm } from '../api';
import { getDisplayError } from '../apiErrors';
import { unwrapData } from '@wapharma/shared';
import { getToken } from '../auth';
import type {
  TaxonomyFacetPreview,
  TaxonomyFacetCommitResult,
  TaxonomyImportRowStatus,
} from './useTaxonomyFacetBulkImport';

function isStructureImportFile(file: File): boolean {
  const n = file.name.toLowerCase();
  return n.endsWith('.xlsx') || n.endsWith('.xls');
}

export type StructureImportPreview = {
  productTypes: TaxonomyFacetPreview;
  categories: TaxonomyFacetPreview;
  summary: {
    productTypesReady: number;
    categoriesReady: number;
    ready: number;
    invalid: number;
    exists: number;
    skipped: number;
  };
  canCommit: boolean;
};

export type StructureImportCommitResult = {
  productTypes: TaxonomyFacetCommitResult;
  categories: TaxonomyFacetCommitResult;
};

export type StructurePreviewSection = 'product-types' | 'categories';

export type StructureFlatPreviewRow = {
  section: StructurePreviewSection;
  row: number;
  status: TaxonomyImportRowStatus;
  issues: string[];
  primary: string;
  secondary: string;
};

export async function downloadStructureImportTemplate(token: string) {
  await apiDownloadBlob(
    '/catalog/admin/structure/import/template',
    token,
    'modele-import-structure.xlsx',
  );
}

export function useStructureBulkImport(opts?: {
  onCommitted?: (result: StructureImportCommitResult) => void;
}) {
  const token = getToken();
  const fileRef = useRef<HTMLInputElement>(null);
  const fileStateRef = useRef<File | null>(null);
  const onCommittedRef = useRef(opts?.onCommitted);
  onCommittedRef.current = opts?.onCommitted;
  const basePath = '/catalog/admin/structure/import';

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<StructureImportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pickErr, setPickErr] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<StructureImportCommitResult | null>(null);

  const reset = useCallback(() => {
    fileStateRef.current = null;
    setFile(null);
    setPreview(null);
    setErr(null);
    setPickErr(null);
    setCommitResult(null);
    setPreviewLoading(false);
    setCommitting(false);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const runPreview = useCallback(
    async (next: File) => {
      if (!token) {
        setErr('Session expirée. Reconnectez-vous pour analyser le fichier.');
        setPreview(null);
        return;
      }
      setPreviewLoading(true);
      setErr(null);
      setCommitResult(null);
      try {
        const form = new FormData();
        form.append('file', next);
        const raw = await apiPostForm<{ data: StructureImportPreview }>(
          `${basePath}/preview`,
          form,
          token,
        );
        setPreview(unwrapData(raw) as StructureImportPreview);
      } catch (e: unknown) {
        setPreview(null);
        setErr(getDisplayError(e));
      } finally {
        setPreviewLoading(false);
      }
    },
    [token],
  );

  const applyFile = useCallback(
    (next: File | null) => {
      setPickErr(null);
      setErr(null);
      setCommitResult(null);
      setPreview(null);
      if (!next) {
        fileStateRef.current = null;
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
        return;
      }
      if (!isStructureImportFile(next)) {
        setPickErr('Format accepté : .xlsx ou .xls (deux feuilles).');
        fileStateRef.current = null;
        setFile(null);
        return;
      }
      fileStateRef.current = next;
      setFile(next);
      void runPreview(next);
    },
    [runPreview],
  );

  const commit = useCallback(async () => {
    const current = fileStateRef.current;
    if (!current) {
      setErr('Fichier requis.');
      return;
    }
    if (!token) {
      setErr('Session expirée.');
      return;
    }
    setCommitting(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append('file', current);
      const raw = await apiPostForm<{ data: StructureImportCommitResult }>(
        `${basePath}/commit`,
        form,
        token,
      );
      const result = unwrapData(raw) as StructureImportCommitResult;
      setCommitResult(result);
      onCommittedRef.current?.(result);
    } catch (e: unknown) {
      setErr(getDisplayError(e));
    } finally {
      setCommitting(false);
    }
  }, [token]);

  const busy = previewLoading || committing;
  const zoneDisabled = !token || busy;
  const canCommit = Boolean(preview?.canCommit) && !busy && !commitResult;

  return {
    token,
    fileRef,
    file,
    preview,
    previewLoading,
    committing,
    busy,
    err,
    pickErr,
    commitResult,
    zoneDisabled,
    canCommit,
    reset,
    applyFile,
    commit,
  };
}
